import React, { useEffect, useState } from "react";
import Layout from "../../layouts/layout";
import classes from "./index.module.css";

import routines from "../../routines";

const backendUrl =
  process.env.REACT_APP_BACKEND_URL || "https://www.dytechlab.com";

function buildPositionMetadata(positionsArray) {
  const positionToCategory = {};
  const positionOrder = {};
  const categories = [];

  positionsArray.forEach((categoryObj, categoryIndex) => {
    categories.push(categoryObj.category);
    categoryObj.positions.forEach((pos, posIndex) => {
      positionToCategory[pos.key] = categoryObj.category;
      positionOrder[pos.key] = {
        categoryIndex,
        positionIndex: posIndex,
      };
    });
  });

  return { positionToCategory, positionOrder, categories };
}

function buildTypesObject(typesArray) {
  const typesObj = {};
  typesArray.forEach((type) => {
    typesObj[type.key] = type.display_name;
  });
  return typesObj;
}

function useFetchOpenJob() {
  const [fetching, setFetching] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setFetching(true);
      try {
        const res = await fetch(routines.career.api.careerData.url);
        if (res.ok) {
          setResponse(await res.json());
        } else {
          throw new Error(`Career API request failed with status ${res.status}`);
        }
      } catch (err) {
        setError(err);
      } finally {
        setFetching(false);
      }
    })();
  }, []);

  return [fetching, response, error];
}

export default function Career() {
  const [jobs, setJobs] = useState([]);
  const [types, setTypes] = useState({});
  const [workplaces, setWorkplaces] = useState({});
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [currJobId, setCurrJobId] = useState(null);
  let [fetching, res, err] = useFetchOpenJob();
  const [typeCount, setTypeCount] = useState({});
  const [highlightStyle, setHighlightStyle] = useState({});
  const [positionMetadata, setPositionMetadata] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(new Set());

  useEffect(() => {
    const onResize = () => {
      setFilterHighlight(selectedType);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [selectedType]);

  useEffect(() => {
    if (fetching === false && (res !== null || err !== null)) {
      if (res !== null) {
        const data = (res.open_jobs || []).map((j) => {
          const job = {...j};

          job.jd = JSON.parse(job.description);
          job.urls = job.workplace.split(",").map((l) => {
            l = l.trim();
            const paramStr = [
              "position=" + encodeURIComponent(job.position),
              "typ=" + encodeURIComponent(job.typ),
              "workplace=" + encodeURIComponent(l),
            ].join("&");
            return [
              l,
              backendUrl +
                routines.career.onlineApplication.url +
                "?" +
                paramStr,
            ];
          });

          return job;
        });
        setJobs(data);
        setWorkplaces(res.workplaces);

        if (Array.isArray(res.positions)) {
          const metadata = buildPositionMetadata(res.positions);
          setPositionMetadata(metadata);
        }

        if (Array.isArray(res.types)) {
          setTypes(buildTypesObject(res.types));
        } else {
          let metaData = res.meta_data;
          if (
            metaData?.type_order !== undefined &&
            metaData.type_order.length > 0
          ) {
            const types = res.types;
            let newTypes = {};
            metaData.type_order.forEach((typeName) => {
              if (types.hasOwnProperty(typeName)) {
                newTypes[typeName] = types[typeName];
              }
            });
            Object.entries(types).forEach(([name, value]) => {
              if (!newTypes.hasOwnProperty(name)) {
                newTypes[name] = value;
              }
            });
            setTypes(newTypes);
          } else {
            setTypes(res.types);
          }
        }
      }
    }
  }, [fetching, res, err]);

  useEffect(() => {
    if (selectedType === null && Object.entries(types).length > 0) {
      setSelectedType(Object.entries(types)[0][0]);
    }
  }, [types, selectedType]);

  useEffect(() => {
    if (positionMetadata?.categories) {
      setSelectedCategories(new Set(positionMetadata.categories));
    }
  }, [positionMetadata]);

  useEffect(() => {
    let filtered = jobs.filter((job) => {
      const jobCategory = positionMetadata?.positionToCategory[job.position];
      return (
        job.typ === selectedType &&
        (selectedCategories.size === 0 || selectedCategories.has(jobCategory))
      );
    });

    filtered.sort((a, b) => {
      const orderA = positionMetadata?.positionOrder[a.position];
      const orderB = positionMetadata?.positionOrder[b.position];

      if (!orderA || !orderB) return 0;

      if (orderA.categoryIndex !== orderB.categoryIndex) {
        return orderA.categoryIndex - orderB.categoryIndex;
      }
      return orderA.positionIndex - orderB.positionIndex;
    });

    setSelectedJobs(filtered);
    setFilterHighlight(selectedType);
  }, [selectedType, selectedCategories, jobs, positionMetadata]);

  useEffect(() => {
    setCurrJobId(selectedJobs[0]?.id);
  }, [selectedJobs]);

  useEffect(() => {
    let typeCount = {};
    jobs.forEach((job) => {
      if (typeCount.hasOwnProperty(job.typ)) {
        typeCount[job.typ]++;
      } else {
        typeCount[job.typ] = 1;
      }
    });
    setTypeCount(typeCount);
  }, [jobs]);

  const onSelectJob = (id) => {
    if (currJobId !== id) {
      setCurrJobId(id);
    }
  };

  const setFilterHighlight = (selectedType) => {
    if (selectedType !== null) {
      const elId = `filter-${selectedType}`;
      const el = document.getElementById(elId);
      if (el === null || el.parentElement === null) {
        return;
      }

      const clientRect = el.getBoundingClientRect();
      const parentClientRect = el.parentElement.getBoundingClientRect();
      setHighlightStyle({
        width: `${clientRect.width}px`,
        left: `${clientRect.left - parentClientRect.left}px`,
      });
    }
  };

  const onClickFilter = (selectedType) => {
    return () => {
      setSelectedType(selectedType);
    };
  };

  const BodyContent = (props) => {
    return (
      <div
        className={classes.body}
        style={{ marginTop: "100px", marginBottom: "100px" }}
      >
        <div className={classes.careers}>
          {/* <div class='alert alert-info' role='alert'>
      <p>If you are participating in our 2025-2026 school Recruitment Drive for full-time positions, please <a target="_blank" href={ routines.recruitmentcampaign.url } class='btn btn-primary' rel="noreferrer">click here</a> to submit your application.</p>
      <p>Alternatively, if you are applying for our internship positions or other full-time positions, please choose the relevant job opening and submit your application on this page.</p>
      </div> */}
          <div className={classes.careers_body}>
            <div className={classes.filterButtonGroup}>
              {Object.entries(types).map(([name, label]) => (
                <div
                  key={`key-${name}`}
                  id={`filter-${name}`}
                  className={`${classes.filterButton} ${name === selectedType ? classes.filterButtonActive : null}`}
                  onClick={onClickFilter(name)}
                >
                  {label}{" "}
                  <span
                    className={`${classes.badge} ${classes.badgeFilterButton}`}
                  >
                    {typeCount[name] || 0}
                  </span>
                </div>
              ))}
              <span
                className={classes.filterHighlight}
                style={highlightStyle}
                key="filterHighlight"
                id="filterHightlight"
              />
            </div>

            {positionMetadata?.categories && (
              <div className={classes.categoryFilter}>
                <div className={classes.categoryFilterHeader}>
                  <div className={classes.categoryFilterLabel}>Category:</div>
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      setSelectedCategories(
                        new Set(positionMetadata.categories),
                      )
                    }
                  >
                    All
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setSelectedCategories(new Set())}
                  >
                    None
                  </button>
                </div>
                <div className={classes.categoryCheckboxGroup}>
                  {positionMetadata.categories.map((category) => (
                    <label key={category} className={classes.categoryCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedCategories.has(category)}
                        onChange={(e) => {
                          const newCategories = new Set(selectedCategories);
                          if (e.target.checked) {
                            newCategories.add(category);
                          } else {
                            newCategories.delete(category);
                          }
                          setSelectedCategories(newCategories);
                        }}
                      />
                      {category}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className={classes.container}>
              <div className={classes.listing}>
                {selectedCategories.size === 0 ? (
                  <div className={classes.emptyState}>
                    <p>No categories are selected.</p>
                    <p>Please select at least one category to view jobs.</p>
                  </div>
                ) : selectedJobs.length === 0 ? (
                  <div className={classes.emptyState}>
                    <p>No jobs match your current filters.</p>
                    <p>Try selecting different categories or job types.</p>
                  </div>
                ) : (
                  selectedJobs.map((job) => (
                    <div
                      onClick={() => onSelectJob(job.id)}
                      key={`jobTitleKey${job.id}`}
                      className={
                        currJobId === job.id ? classes.activeTitle : null
                      }
                    >
                      <div
                        className={classes.job_card}
                        id={`id${job.id}`}
                        key={job.id}
                      >
                        <h3 className={`heading ${classes.sidebar_item_title}`}>
                          {job.jd?.title}
                        </h3>

                        <div>
                          <p>
                            {job.urls.map(([l, _]) => workplaces[l]).join(", ")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className={classes.container_right}>
                {selectedCategories.size === 0 || selectedJobs.length === 0 ? (
                  <div className={classes.emptyState}>
                    <p>Select a job to view details</p>
                  </div>
                ) : (
                  jobs.map((job) => {
                    return (
                      <div
                        id={`job_${job.id}_id`}
                        key={`job_${job.id}`}
                        className={
                          currJobId === job.id ? classes.selectedJob : null
                        }
                        style={{
                          top: "120px",
                          display: currJobId === job.id ? "block" : "none",
                        }}
                      >
                        <div
                          style={{ paddingRight: "20px", paddingLeft: "13px" }}
                        >
                          <h2>{job.jd?.title}</h2>
                          <br></br>

                          <h3>Locations</h3>
                          <table>
                            <tbody>
                              {job.urls.map(([location, link]) => (
                                <tr
                                  key={`apply_link_${location}`}
                                  style={{ fontSize: "1rem" }}
                                >
                                  <td
                                    style={{ margin: "10px", padding: "10px" }}
                                  >
                                    {workplaces[location]}
                                  </td>
                                  <td
                                    style={{ margin: "10px", padding: "10px" }}
                                  >
                                    <a
                                      className="btn btn-primary"
                                      href={link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Apply Now
                                    </a>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <br></br>

                          {job.jd.description?.length > 0 && (
                            <>
                              <h3>Job Description</h3>
                              {job.jd.description.map((desc, i) => {
                                if (typeof desc === "string") {
                                  return (
                                    <p
                                      className="para"
                                      style={{
                                        lineHeight: "1.4",
                                        fontSize: "1rem",
                                      }}
                                      key={i}
                                    >
                                      {desc}
                                    </p>
                                  );
                                } else if (
                                  typeof desc === "object" &&
                                  desc.length > 0
                                ) {
                                  return (
                                    <div key={`desc-key-${i}`}>
                                      {desc.map((resp, i) => (
                                        <p
                                          className="para"
                                          style={{
                                            lineHeight: "1.4",
                                            fontSize: "1rem",
                                          }}
                                          key={i}
                                        >
                                          <span className={classes.dot}></span>
                                          {resp}
                                        </p>
                                      ))}
                                    </div>
                                  );
                                }
                              })}
                              <br></br>
                            </>
                          )}
                          {job.jd.qualification?.length > 0 && (
                            <>
                              <h3>Qualifications</h3>
                              <div>
                                {job.jd.qualification.map((qual, i) => {
                                  if (typeof qual === "string") {
                                    return (
                                      <p
                                        className="para"
                                        style={{
                                          lineHeight: "1.4",
                                          fontSize: "1rem",
                                        }}
                                        key={i}
                                      >
                                        {qual}
                                      </p>
                                    );
                                  } else if (
                                    typeof qual === "object" &&
                                    qual.length > 0
                                  ) {
                                    return (
                                      <div key={`qual-key-${i}`}>
                                        {qual.map((resp, i) => (
                                          <p
                                            className="para"
                                            style={{
                                              lineHeight: "1.4",
                                              fontSize: "1rem",
                                            }}
                                            key={i}
                                          >
                                            <span
                                              className={classes.dot}
                                            ></span>
                                            {resp}
                                          </p>
                                        ))}
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                              <br></br>
                            </>
                          )}

                          {job.jd.plus?.length > 0 && (
                            <>
                              <h3>Pluses</h3>
                              <div style={{ marginBottom: "20px" }}>
                                {job.jd.plus.map((plus, i) => {
                                  if (typeof plus === "string") {
                                    return (
                                      <p
                                        className="para"
                                        style={{
                                          lineHeight: "1.4",
                                          fontSize: "1rem",
                                        }}
                                        key={i}
                                      >
                                        {plus}
                                      </p>
                                    );
                                  } else if (
                                    typeof plus === "object" &&
                                    plus.length > 0
                                  ) {
                                    return (
                                      <div key={`plus-key-${i}`}>
                                        {plus.map((resp, i) => (
                                          <p
                                            className="para"
                                            style={{
                                              lineHeight: "1.4",
                                              fontSize: "1rem",
                                            }}
                                            key={i}
                                          >
                                            <span
                                              className={classes.dot}
                                            ></span>
                                            {resp}
                                          </p>
                                        ))}
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                              <br></br>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout
      bodyContent={<BodyContent />}
      bannerImage="/images/career/0.jpg"
      bannerImageAlt="career-banner-image"
      bannerSmallImage="/images/career/0.jpg"
      bannerMainTitle={<p>Work with us</p>}
      bannerSubTitle={
        <p>
          We offer opportunities for our people to realise their full potential.
        </p>
      }
    />
  );
}
